(() => {
  const NAME = "fgwp-controller";

  if (customElements.get(NAME)) return;

  const REWARD_FLAG = "yes";

  /**
   * Free Gift With Purchase — storefront controller (CU-869dfj1az).
   *
   * Owns which reward lines EXIST. It does not own their price: the discount
   * function does, and it re-verifies everything this file writes. So a bug
   * here can put a chargeable gift in the bag, but it cannot give away stock.
   *
   * Reconciliation is single-flight. Every cart mutation queues behind the
   * previous one on a promise tail, because /cart/change.js and /cart/add.js
   * are not atomic against each other — two overlapping passes are how
   * duplicate gift lines appear.
   *
   * That tail is module-wide, not per-element. The cart page and the cart
   * drawer each render a controller, and on /cart both are in the DOM at the
   * same time — two elements running their own passes against one cart is the
   * same race by another route.
   *
   * It does NOT render the gift card. snippets/fgwp-gift-cards.liquid does,
   * server-side, with the rest of the cart. Building it here meant a second
   * /cart.js round trip after the section had already painted, so the bag
   * showed its line items and then the gift a beat later. This file only
   * handles the shopper's choice, the add, and evicting a reward line that is
   * no longer earned.
   */
  const instances = new Set();

  // Chosen variant per tier level, module-wide so the cart page and the drawer
  // cannot show different selections for the same gift, and so a section
  // re-render does not lose what the shopper picked.
  const selected = new Map();

  // Chosen colour per tier level. A colour is a PRODUCT — the catalogue is one
  // product per colourway — so switching colour swaps the card's whole panel:
  // title, price, sizes, ADD. Module-wide for the same two reasons as above.
  const selectedColour = new Map();

  /**
   * Show one colour's panel and media frame on a card and press its dots.
   * Dots are duplicated inside every panel so the arriving panel is already
   * painted; pressing by data-colour-target keeps each copy in agreement.
   */
  function paintColour(card, colour) {
    for (const node of card.querySelectorAll(
      "[data-fgwp-panel], [data-fgwp-media]",
    )) {
      node.hidden = String(node.dataset.colour) !== String(colour);
    }
    for (const dot of card.querySelectorAll("[data-fgwp-colour]")) {
      dot.setAttribute(
        "aria-pressed",
        String(String(dot.dataset.colourTarget) === String(colour)),
      );
    }

    // A colour swap abandons any size armed on another panel, so every panel
    // that offers a size choice goes back to its rendered state — chips
    // unpressed, ADD disarmed, compare price restored. A single-variant panel
    // has no chips and keeps its ready ADD. #applySelection re-arms a size
    // that is still remembered right after this runs.
    for (const panel of card.querySelectorAll("[data-fgwp-panel]")) {
      const chips = panel.querySelectorAll("[data-fgwp-choose]");
      if (chips.length === 0) continue;

      for (const chip of chips) {
        chip.setAttribute("aria-pressed", "false");
        chip.classList.remove("fgwp-card__size--selected");
      }

      const add = panel.querySelector("[data-fgwp-add]");
      if (add) {
        add.disabled = true;
        add.classList.remove("fgwp-card__add--ready");
        delete add.dataset.variant;
      }

      const compare = panel.querySelector("[data-fgwp-compare]");
      if (compare?.dataset.fgwpInitial != null) {
        compare.textContent = compare.dataset.fgwpInitial;
      }
    }
  }

  let tail = Promise.resolve();
  let busy = false;

  /*
   * Both surfaces reconcile independently, so the flag is refcounted: the
   * drawer finishing must not unblock the page while the page is still
   * removing a line. On the document element, because a section re-render
   * replaces everything inside it.
   */
  let settlingCount = 0;
  function markSettling(delta) {
    settlingCount = Math.max(0, settlingCount + delta);
    document.documentElement.toggleAttribute(
      "data-fgwp-settling",
      settlingCount > 0,
    );
  }

  class FgwpController extends HTMLElement {
    #config = null;
    #abort = null;

    connectedCallback() {
      this.#abort = new AbortController();
      this.#config = this.#readConfig();
      if (!this.#config?.tiers?.length) return;

      instances.add(this);

      this.addEventListener("click", this.#onClick, {
        signal: this.#abort.signal,
      });

      // Any cart mutation anywhere on the page re-runs reconciliation.
      document.addEventListener("cart:updated", () => this.reconcile(), {
        signal: this.#abort.signal,
      });

      // The cards arrived with the section HTML, so this runs against markup
      // that is already painted rather than building it.
      this.#applySelection();

      this.reconcile();
    }

    /**
     * Liquid renders the first variant selected, because it has no idea what
     * the shopper picked. Re-apply the choice after every re-render.
     */
    #applySelection() {
      for (const card of this.querySelectorAll(".fgwp-card")) {
        const level = String(card.dataset.fgwpTier);

        // Colour first: it decides which panel the size choice lives in.
        // Liquid renders the first colour visible, so only a remembered
        // choice needs repainting.
        const colour = selectedColour.get(level);
        if (colour != null) paintColour(card, colour);

        const chosen = selected.get(level);
        if (!chosen) continue;

        const chip = card.querySelector(
          `[data-fgwp-choose][data-variant="${CSS.escape(chosen)}"]`,
        );
        if (!chip) continue;

        for (const option of card.querySelectorAll("[data-fgwp-choose]")) {
          const isChosen = option === chip;
          option.setAttribute("aria-pressed", String(isChosen));
          option.classList.toggle("fgwp-card__size--selected", isChosen);
        }

        // Scoped to the chip's own panel: with one panel per colour, a
        // card-wide query would arm the first panel's ADD, not this one's.
        const panel = chip.closest("[data-fgwp-panel]") ?? card;

        const add = panel.querySelector("[data-fgwp-add]");
        if (add) {
          add.dataset.variant = chosen;
          add.disabled = false;
          add.classList.add("fgwp-card__add--ready");
        }

        const compare = panel.querySelector("[data-fgwp-compare]");
        if (compare && chip.dataset.price) {
          compare.dataset.fgwpInitial ??= compare.textContent;
          compare.textContent = chip.dataset.price;
        }
      }
    }

    disconnectedCallback() {
      instances.delete(this);
      this.#abort?.abort();
      this.#abort = null;
    }

    #readConfig() {
      const node = this.querySelector("[data-fgwp-config]");
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch {
        return null;
      }
    }

    /**
     * Presentment threshold for a tier.
     *
     * `minShopMinor` is shop-currency minor units and is part of the signed
     * tuple, so it must never be converted before signing. Shopify.currency.rate
     * is the storefront twin of the function's presentmentCurrencyRate — the two
     * must agree or the bar promises a gift the pricing refuses.
     */
    #thresholdPresentmentMinor(tier) {
      const rate = Number(window.Shopify?.currency?.rate ?? 1) || 1;
      return Math.round(tier.minShopMinor * rate);
    }

    /**
     * Qualifying value excludes reward lines — by their flag, not their price.
     * On the storefront a reward line still carries the variant's real price
     * until checkout recalculates, so filtering on price would count it.
     */
    #qualifyingMinor(cart) {
      return cart.items.reduce((total, item) => {
        if (item.properties?._reward === REWARD_FLAG) return total;
        return total + item.final_line_price;
      }, 0);
    }

    async #getCart() {
      const res = await fetch(`${window.Routes?.cart_url ?? "/cart"}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("cart read failed");
      return res.json();
    }

    /**
     * Hash signer URL. On an unpublished preview the live theme has no
     * `search.fgwp-hash` view, so the request must carry preview_theme_id
     * or it comes back as HTML and auto-add fails silently.
     */
    #hashRequestUrl(tier, variantId) {
      const url = new URL(this.#config.hashUrl, window.location.origin);
      url.searchParams.set(
        "q",
        `${tier.level}--${variantId}--${tier.minShopMinor}`,
      );

      const previewFromPage = new URLSearchParams(window.location.search).get(
        "preview_theme_id",
      );
      const unpublishedId =
        window.Shopify?.theme?.role && window.Shopify.theme.role !== "main"
          ? window.Shopify.theme.id
          : null;
      const preview = previewFromPage || unpublishedId;

      if (preview && !url.searchParams.get("preview_theme_id")) {
        url.searchParams.set("preview_theme_id", String(preview));
      }

      return url.toString();
    }

    /** Server-signed hash for a (tier, variant, min) tuple. */
    async #sign(tier, variantId) {
      const res = await fetch(this.#hashRequestUrl(tier, variantId), {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("sign failed");
      const data = await res.json();
      if (!data?.hash) throw new Error("sign returned no hash");
      return data.hash;
    }

    async #addReward(tier, variantId) {
      const hash = await this.#sign(tier, variantId);
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                _reward: REWARD_FLAG,
                _tier_level: String(tier.level),
                _tier_min: String(tier.minShopMinor),
                _hash: hash,
              },
            },
          ],
        }),
      });
      if (!res.ok) throw new Error("reward add failed");
    }

    async #removeLine(key) {
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      if (!res.ok) throw new Error("reward remove failed");
    }

    /** Queue work so two passes can never interleave, on any surface. */
    reconcile() {
      const next = tail.catch(() => {}).then(() => this.#reconcileOnce());
      tail = next;
      return next;
    }

    async #reconcileOnce() {
      if (!this.#config?.tiers?.length) return;

      /*
       * A reward line the function did not zero is already on screen as
       * "leaving" — Liquid marks it, because the price it renders IS the
       * server's verdict. Until this pass removes it, though, Checkout stayed
       * live, so a shopper who dropped below a tier and clicked straight away
       * carried an unearned gift into checkout (raised in review on PR #141).
       * Nothing could be had for free — the function refuses it and the
       * checkout extension sweeps it — but they could be charged for it.
       *
       * So the CTA is held for exactly as long as the removal takes. Raised
       * only when such a line is on screen, so an ordinary quantity change
       * never touches it, and released in a finally so a failure here can
       * never leave the bag permanently un-checkout-able. A dead script
       * cannot raise it at all.
       */
      const settling = Boolean(document.querySelector("[data-reward-leaving]"));
      if (settling) markSettling(1);

      try {
        return await this.#reconcileCart();
      } finally {
        if (settling) markSettling(-1);
      }
    }

    async #reconcileCart() {
      let cart;
      try {
        cart = await this.#getCart();
      } catch {
        return;
      }

      /*
       * Stamp the presentment rate onto the cart for the checkout extension.
       * Checkout's API exposes no exchange rate at all — no
       * presentmentCurrencyRate, no shop-currency money, nothing derivable:
       * this store's EUR market prices from a price list, so a
       * presentment/shop price ratio comes out at 1.26 against the real rate of
       * 1.19 and would disagree with the discount function.
       *
       * The currency is stamped WITH it, because a buyer can change country at
       * checkout and switch currency. A rate on its own would silently go stale
       * at that point and start authorising the wrong thresholds; paired with
       * the currency it was measured in, the extension can tell that it no
       * longer applies and decline to offer instead.
       *
       * Written under BOTH keys, prefixed and not. A checkout UI extension is
       * only handed buyer-visible cart attributes, and an `_` prefix marks an
       * attribute private — so `_fgwp_rate` alone never reaches the extension,
       * which then has no rate outside the shop's own currency and offers
       * nothing at all. The un-prefixed pair is the one it can actually read;
       * the cost is that it shows on the order, which is the accepted trade for
       * the gift appearing in every market.
       *
       * Written only when a value changes, so it cannot loop.
       */
      const rate = String(Number(window.Shopify?.currency?.rate ?? 1) || 1);
      const currency = String(window.Shopify?.currency?.active ?? "");
      let rateWritten = false;

      if (
        String(cart.attributes?._fgwp_rate ?? "") !== rate ||
        String(cart.attributes?._fgwp_currency ?? "") !== currency ||
        String(cart.attributes?.fgwp_rate ?? "") !== rate ||
        String(cart.attributes?.fgwp_currency ?? "") !== currency
      ) {
        try {
          await fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              attributes: {
                _fgwp_rate: rate,
                _fgwp_currency: currency,
                fgwp_rate: rate,
                fgwp_currency: currency,
              },
            }),
          });
          rateWritten = true;
        } catch {
          /* checkout declines to offer rather than guess — see the extension */
        }
      }

      const qualifying = this.#qualifyingMinor(cart);
      let mutated = false;
      const removed = new Set();

      /*
       * A reward line the discount function did not zero is not a gift — the
       * shopper is being charged for it. The function re-verifies eligibility
       * on every cart change, so a non-zero price IS the server telling us the
       * line is no longer earned, whatever the reason: the spend dropped when
       * a paid item was removed, the hash was rejected, the tier was retired
       * from settings. Trusting that instead of re-deriving the thresholds here
       * is why this runs first, and why it cannot miss a case the arithmetic
       * below does not know about.
       */
      for (const item of cart.items) {
        if (item.properties?._reward !== REWARD_FLAG) continue;
        if (Number(item.final_line_price) === 0) continue;

        await this.#removeLine(item.key);
        removed.add(item.key);
        mutated = true;
      }

      for (const tier of this.#config.tiers) {
        const level = String(tier.level);
        const held = cart.items.filter(
          (item) =>
            item.properties?._reward === REWARD_FLAG &&
            !removed.has(item.key) &&
            String(item.properties?._tier_level) === level,
        );

        const eligible = qualifying >= this.#thresholdPresentmentMinor(tier);
        const allowed = new Set(tier.choices.map((c) => String(c.variantId)));

        // Evict anything stale: not eligible any more, a colour that has been
        // retired from config, or a duplicate beyond the first. Keeping the
        // first means a shopper's chosen colour survives an unrelated change.
        const keep = [];
        for (const item of held) {
          const staleVariant = !allowed.has(String(item.variant_id));
          if (!eligible || staleVariant || keep.length >= 1) {
            await this.#removeLine(item.key);
            mutated = true;
            continue;
          }
          keep.push(item);
        }

        // Nothing is ever added automatically. The design gives every gift an
        // ADD button, so an eligible tier with no line held renders the
        // selector and waits — and the absence of a line IS the unselected
        // state, which is what makes a manual removal recoverable.
      }

      if (mutated) {
        document.dispatchEvent(
          new CustomEvent("fgwp:changed", { bubbles: true }),
        );
      }

      // The threshold bar and the cards convert their amounts with the rate
      // attribute, so the first cart to receive it has to re-render or the bar
      // keeps the fallback of 1. The next pass sees a matching attribute and
      // stops, so this cannot loop.
      if (mutated || rateWritten) this.#refresh();
    }

    #refresh() {
      // Let the host surface re-render from the server. The cart page and drawer
      // both listen for this; a full reload is the fallback when neither does.
      const handled = !document.dispatchEvent(
        new CustomEvent("fgwp:refresh", { bubbles: true, cancelable: true }),
      );
      if (!handled) window.location.reload();
    }

    #onClick = (event) => {
      const dot = event.target.closest("[data-fgwp-colour]");
      if (dot) {
        event.preventDefault();
        if (busy) return;

        const level = String(dot.dataset.tier);
        const colour = String(dot.dataset.colourTarget);

        // Re-clicking the colour already showing is not a change, and the
        // repaint below would clear a size chosen on its panel.
        if (dot.getAttribute("aria-pressed") === "true") return;

        selectedColour.set(level, colour);

        // A different colour is a different PRODUCT, so any size chosen on
        // the previous panel no longer applies to it.
        selected.delete(level);

        // Local repaint on every surface showing this tier — /cart has two.
        for (const card of document.querySelectorAll(
          `.fgwp-card[data-fgwp-tier="${CSS.escape(level)}"]`,
        )) {
          paintColour(card, colour);
        }
        return;
      }

      const chip = event.target.closest("[data-fgwp-choose]");
      if (chip) {
        event.preventDefault();
        if (busy) return;

        const level = String(chip.dataset.tier);
        selected.set(level, String(chip.dataset.variant));

        // Choosing a size touches no cart state, so it is a local repaint —
        // every surface showing this tier, because /cart has two of them.
        for (const card of document.querySelectorAll(
          `.fgwp-card[data-fgwp-tier="${CSS.escape(level)}"]`,
        )) {
          const twin = card.querySelector(
            `[data-fgwp-choose][data-variant="${CSS.escape(
              String(chip.dataset.variant),
            )}"]`,
          );
          if (!twin) continue;

          for (const option of card.querySelectorAll("[data-fgwp-choose]")) {
            const isChosen = option.dataset.variant === chip.dataset.variant;
            option.setAttribute("aria-pressed", String(isChosen));
            option.classList.toggle("fgwp-card__size--selected", isChosen);
          }

          // Panel-scoped: with one panel per colour, the card-wide query used
          // to arm whichever panel came first in the DOM.
          const panel = twin.closest("[data-fgwp-panel]") ?? card;

          const compare = panel.querySelector("[data-fgwp-compare]");
          if (compare && chip.dataset.price) {
            compare.dataset.fgwpInitial ??= compare.textContent;
            compare.textContent = chip.dataset.price;
          }

          // A size is now chosen, so ADD becomes actionable and its disc fills.
          const add = panel.querySelector("[data-fgwp-add]");
          if (add) {
            add.dataset.variant = String(chip.dataset.variant);
            add.disabled = false;
            add.classList.add("fgwp-card__add--ready");
          }
        }
        return;
      }

      const button = event.target.closest("[data-fgwp-add]");
      if (!button || busy) return;

      event.preventDefault();
      const tier = this.#config.tiers.find(
        (t) => String(t.level) === button.dataset.tier,
      );
      if (!tier) return;

      const variantId =
        selected.get(String(tier.level)) || button.dataset.variant;
      if (!variantId) return;

      busy = true;
      const level = CSS.escape(String(tier.level));
      const cards = document.querySelectorAll(
        `.fgwp-card[data-fgwp-tier="${level}"]`,
      );

      for (const instance of instances) {
        instance.setAttribute("aria-busy", "true");
      }
      // The disc becomes a spinner on the card that was pressed, on every
      // surface showing this tier — /cart has two of them.
      for (const card of cards) {
        card.classList.add("fgwp-card--adding");
        // The visible panel's status line — each colour panel carries its own.
        const status =
          card.querySelector(
            "[data-fgwp-panel]:not([hidden]) [data-fgwp-add-status]",
          ) ?? card.querySelector("[data-fgwp-add-status]");
        if (status) status.textContent = this.#config.addingLabel || "";
      }

      tail = tail
        .catch(() => {})
        .then(() => this.#addReward(tier, variantId))
        .catch(() => {})
        .then(() => {
          busy = false;
          for (const instance of instances) {
            instance.removeAttribute("aria-busy");
          }
          this.#refresh();
        });
    };
  }

  customElements.define(NAME, FgwpController);
})();
